import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Phone, MessageCircle, Search, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const ENV = { production: "الإنتاج", simulation: "المحاكاة", sandbox: "المطورين" };
const REQ = { new: "جديد", in_progress: "قيد التنفيذ", done: "تم الربط", cancelled: "ملغي" };
const FILTERS = [["all", "الكل"], ["support", "تحتاج دعم"], ["requests", "طلبات ربط"], ["live", "مربوطة فعلياً"]];
const wa = (p) => { const n = String(p || "").replace(/\D/g, ""); return n.startsWith("05") ? `966${n.slice(1)}` : n; };

const StatusPill = ({ r }) => {
  if (r.live) return <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">مربوطة · الإنتاج</span>;
  if (r.zatca_status === "not_started") return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">غير مربوطة</span>;
  return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">{r.zatca_status === "active" ? "مفعّل" : "قيد الربط"} · {ENV[r.environment] || r.environment || "—"}</span>;
};

const Row = ({ r, reload }) => {
  const q = r.request;
  const setStatus = async (s) => { try { await api.adminZatcaRequestStatus(q.id, s); toast.success("تم التحديث"); reload(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التحديث"); } };
  const soon = r.cert_expires && (new Date(r.cert_expires) - new Date()) / 864e5 < 30;
  return (
    <div className={`rounded-xl border p-3 text-sm ${q?.status === "new" ? "border-amber-300 bg-amber-50/50" : r.needs_support ? "border-slate-200" : "border-emerald-200 bg-emerald-50/30"}`} data-testid={`zatca-co-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1"><div className="font-bold text-slate-800">{r.name}</div>
          <div className="text-[11px] text-slate-500 font-mono-nums">{r.vat_number || "بدون رقم ضريبي"}{r.cert_expires ? ` · الشهادة حتى ${r.cert_expires}` : ""}</div></div>
        <StatusPill r={r} />
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-600">
        <span>مُرسلة: <b className="text-emerald-700">{r.docs.ok}</b></span>
        <span>معلّقة: <b className={r.docs.pending ? "text-amber-700" : ""}>{r.docs.pending}</b></span>
        <span>مرفوضة: <b className={r.docs.rejected ? "text-rose-700" : ""}>{r.docs.rejected}</b></span>
        {r.live && !r.auto_submit && <span className="font-bold text-rose-700">الإرسال التلقائي مطفأ</span>}
        {soon && <span className="font-bold text-rose-700">الشهادة تنتهي قريباً</span>}
        {r.docs.last && <span>آخر إرسال: {r.docs.last.slice(0, 10)}</span>}
      </div>
      {q && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-white/70 p-2 text-xs" data-testid={`zatca-req-${q.id}`}>
          <span className="font-bold">طلب ربط ({q.created_at?.slice(0, 10)}):</span>
          <span>{q.contact_name}</span>
          <a href={`tel:${q.phone}`} className="inline-flex items-center gap-1 text-blue-700" dir="ltr"><Phone className="h-3 w-3" />{q.phone}</a>
          <a href={`https://wa.me/${wa(q.phone)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700"><MessageCircle className="h-3 w-3" /> واتساب</a>
          {q.note && <span className="w-full text-slate-500">{q.note}</span>}
          <Select value={q.status} onValueChange={setStatus}>
            <SelectTrigger className="h-7 w-32 text-xs" data-testid={`zatca-req-status-${q.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(REQ).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export const ZatcaCompaniesButton = () => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [f, setF] = useState("support");
  const [s, setS] = useState("");
  const load = () => api.adminZatcaCompanies().then(setD).catch(() => toast.error("تعذّر التحميل"));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (open) load(); }, [open]);
  const rows = useMemo(() => (d?.rows || []).filter((r) => (f === "all" || (f === "support" && r.needs_support) || (f === "requests" && r.request && ["new", "in_progress"].includes(r.request.status)) || (f === "live" && r.live))
    && (!s || `${r.name} ${r.vat_number}`.toLowerCase().includes(s.toLowerCase()))), [d, f, s]);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="relative gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="open-zatca-companies">
        <ShieldCheck className="h-4 w-4" /> <span className="hidden sm:inline">ربط الشركات مع ZATCA</span>
        {d?.new_requests > 0 && <span className="absolute -top-1.5 -left-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white" data-testid="zatca-new-req-badge">{d.new_requests}</span>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="zatca-companies-dialog">
          <DialogHeader className="text-start"><DialogTitle className="flex items-center gap-2 ps-8"><ShieldCheck className="h-5 w-5 text-emerald-600" /> ربط الشركات المشتركة مع ZATCA</DialogTitle>
            <DialogDescription>حالة ربط كل شركة في برنامج WorkZone المحاسبي، وطلبات الربط الواردة.</DialogDescription></DialogHeader>
          {!d ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-emerald-600" /> : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[["الشركات", d.total, "text-slate-800"], ["مربوطة فعلياً", d.live, "text-emerald-700"], ["تحتاج دعم", d.needs_support, "text-amber-700"], ["طلبات جديدة", d.new_requests, "text-rose-700"]].map(([l, v, c]) => (
                  <div key={l} className="rounded-xl border border-slate-200 p-3"><div className="text-[11px] text-slate-500">{l}</div><div className={`text-2xl font-extrabold font-mono-nums ${c}`}>{v}</div></div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map(([k, v]) => <button key={k} onClick={() => setF(k)} data-testid={`zatca-filter-${k}`} className={`rounded-lg px-3 py-1.5 text-xs ${f === k ? "bg-slate-900 font-bold text-white" : "border border-slate-200 bg-white text-slate-600"}`}>{v}</button>)}
                <div className="relative flex-1 min-w-[140px]"><Search className="absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input value={s} onChange={(e) => setS(e.target.value)} placeholder="بحث بالاسم أو الرقم الضريبي" className="h-9 pr-8" data-testid="zatca-co-search" /></div>
                <Button size="sm" variant="ghost" onClick={load} aria-label="تحديث"><RefreshCw className="h-4 w-4" /></Button>
              </div>
              {rows.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">لا توجد شركات</p> : rows.map((r) => <Row key={r.id} r={r} reload={load} />)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
